import React, { useEffect, useRef, useState } from 'react';
import './AnimatedTitle.css';

interface AnimatedTitleProps {
  baseText: string;
  swapWords?: {
    from?: string;
    to?: string;
    phrases?: string[]
  };
  className?: string;
  maxLines?: number;
  typewriter?: boolean;
  onSecondLineAppear?: () => void;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  useHTML?: boolean;
}

const AnimatedTitle: React.FC<AnimatedTitleProps> = ({
  baseText,
  swapWords,
  className = '',
  maxLines = 2,
  typewriter = false,
  onSecondLineAppear,
  headingLevel = 'h1',
  useHTML = false
}) => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const secondLineRef = useRef<HTMLHeadingElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const swapRef = useRef<HTMLSpanElement | null>(null);
  const [isScrambling, setIsScrambling] = useState(true);
  const [showingFirst, setShowingFirst] = useState(true);
  const [showSecondLine, setShowSecondLine] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const phraseIndexRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset phraseIndex quando o componente é montado
  useEffect(() => {
    setPhraseIndex(0);
    phraseIndexRef.current = 0;
  }, []);

  const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const isGlyph = (c: string) => /[A-Za-zÀ-ÿ0-9]/.test(c);

  // Preparar o texto com quebra de linha
  let textWithBreak = baseText;
  let lines: string[] = [];

  if (useHTML) {
    // Para HTML, processar corretamente o texto com <br> e <span>
    // Não fazer replace de "diariamente" quando é HTML
    lines = baseText.split('<br>');
  } else {
    // Para texto simples
    textWithBreak = baseText.replace("diariamente ", "diariamente<br>");
    lines = textWithBreak.split('<br>');
  }

  const firstLine = lines[0] || '';
  const secondLine = lines[1] || '';

  const typewriterEffect = (text: string, onComplete: () => void) => {
    if (!titleRef.current) return;

    if (useHTML) {
      // Para HTML, mostrar o texto completo com fade in
      titleRef.current.style.opacity = '0';
      titleRef.current.innerHTML = text;

      // Fade in suave
      let opacity = 0;
      const fadeIn = () => {
        opacity += 0.1;
        if (titleRef.current) {
          titleRef.current.style.opacity = opacity.toString();
        }

        if (opacity < 1) {
          requestAnimationFrame(fadeIn);
        } else {
          // Garantir que permanece visível após a animação
          if (titleRef.current) {
            titleRef.current.style.opacity = '1';
          }
          onComplete();
        }
      };

      // Iniciar fade in após um pequeno delay
      setTimeout(fadeIn, 300);
    } else {
      // Typewriter normal para texto simples
      let i = 0;
      let frameCount = 0;
      const FRAMES_PER_CHAR = 1;

      titleRef.current.style.opacity = '0';
      titleRef.current.textContent = text;

      const tick = () => {
        if (titleRef.current && i <= text.length) {
          titleRef.current.textContent = text.substring(0, i);
          titleRef.current.style.opacity = '1';
        }

        frameCount++;
        if (frameCount >= FRAMES_PER_CHAR) {
          i++;
          frameCount = 0;
        }

        if (i <= text.length) {
          requestAnimationFrame(tick);
        } else {
          onComplete();
        }
      };
      tick();
    }
  };

  const scrambleText = (text: string, onComplete: () => void) => {
    if (!titleRef.current) return;

    let revealedChars = 0;
    let skipFrame = false;

    // Manter o texto original mas invisível para reservar espaço
    titleRef.current.style.opacity = '1';
    titleRef.current.textContent = firstLine;

    const tick = () => {
      let output = "";

      // Primeira linha - sempre com o mesmo número de caracteres
      for (let k = 0; k < firstLine.length; k++) {
        const ch = firstLine[k];
        if (!isGlyph(ch)) {
          output += ch;
        } else {
          if (k < revealedChars) {
            output += ch;
          } else {
            output += ALPHA[Math.floor(Math.random() * ALPHA.length)];
          }
        }
      }

      if (titleRef.current) {
        titleRef.current.textContent = output;
        titleRef.current.style.opacity = '1';
      }

      // Reveal character every other frame
      if (!skipFrame) {
        revealedChars++;
      }
      skipFrame = !skipFrame;

      if (revealedChars <= firstLine.length) {
        requestAnimationFrame(tick);
      } else {
        // Garantir que o texto final está exatamente correto antes da transição
        if (titleRef.current) {
          titleRef.current.textContent = firstLine;
        }
        // Very brief pause before starting word swap
        setTimeout(onComplete, 200);
      }
    };
    tick();
  };

  const initializeSwap = () => {
    if (!titleRef.current || !measureRef.current || !swapWords) return;

    let fromText, toText;

    if (swapWords.phrases && swapWords.phrases.length > 0) {
      // Usar array de frases
      fromText = swapWords.phrases[0];
      toText = swapWords.phrases[1] || swapWords.phrases[0];
    } else if (swapWords.from && swapWords.to) {
      // Usar sistema original
      fromText = swapWords.from;
      toText = swapWords.to;
    } else {
      return; // Não há dados suficientes
    }

    // Calcular larguras ANTES de mudar o HTML
    const computedStyle = getComputedStyle(titleRef.current);
    measureRef.current.style.font = computedStyle.font;

    measureRef.current.textContent = fromText;
    const widthA = measureRef.current.offsetWidth;
    measureRef.current.textContent = toText;
    const widthB = measureRef.current.offsetWidth;
    const maxWidth = Math.max(widthA, widthB);

    // Criar o HTML final com a largura fixa para a primeira linha
    // Substituir todo o texto da primeira linha pelo elemento de swap
    const finalHTML = `<span class="animated-swap" data-from="${fromText}" data-to="${toText}" style="width: ${maxWidth}px; display: inline-block;">${fromText}</span>`;

    // Aplicar HTML imediatamente
    if (!titleRef.current) return;

    titleRef.current.innerHTML = finalHTML;

    // Encontrar o span criado
    const swapElement = titleRef.current.querySelector('.animated-swap') as HTMLSpanElement;
    if (!swapElement) {
      return;
    }

    swapRef.current = swapElement;

    // Aplicar classe imediatamente
    swapElement.classList.add('swap-in');

    // Mostrar a segunda linha
    setShowSecondLine(true);
    if (onSecondLineAppear) {
      onSecondLineAppear();
    }

    setIsScrambling(false);
  };

  const performSwap = () => {
    if (!swapRef.current || !swapWords) return;

    swapRef.current.classList.remove('swap-in');
    swapRef.current.classList.add('swap-out');

    setTimeout(() => {
      if (!swapRef.current || !swapWords) return;

      if (swapWords.phrases && swapWords.phrases.length > 0) {
        // Usar array de frases - apenas avançar para a próxima
        const nextIndex = (phraseIndexRef.current + 1) % swapWords.phrases.length;
        const nextPhrase = swapWords.phrases[nextIndex];

        swapRef.current.textContent = nextPhrase;
        phraseIndexRef.current = nextIndex;
        setPhraseIndex(nextIndex);
      } else if (swapWords.from && swapWords.to) {
        // Usar sistema original
        swapRef.current.textContent = showingFirst ? swapWords.to : swapWords.from;
        setShowingFirst(!showingFirst);
      }

      swapRef.current.classList.remove('swap-out');
      // Force reflow
      void swapRef.current.offsetWidth;
      swapRef.current.classList.add('swap-in');
    }, 200);
  };

  useEffect(() => {
    const startAnimation = () => {
      if (typewriter) {
        typewriterEffect(baseText, () => setIsScrambling(false));
      } else if (swapWords && swapWords.phrases && swapWords.phrases.length > 0) {
        // Para array de frases, não fazer scrambling, ir direto para o swap
        setIsScrambling(false);
        // Inicializar imediatamente
        initializeSwap();
      } else if (swapWords) {
        scrambleText(baseText, initializeSwap);
      } else {
        // Just scramble without word swapping
        scrambleText(baseText, () => setIsScrambling(false));
      }
    };

    // Start animation
    startAnimation();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [baseText, swapWords, typewriter]);

  useEffect(() => {
    if (!isScrambling && swapWords && !typewriter) {
      // Fazer a primeira troca após o tempo normal
      const firstSwapTimer = setTimeout(() => {
        performSwap();

        // Depois da primeira troca, iniciar o intervalo normal
        intervalRef.current = setInterval(performSwap, 3500);
      }, 3000);

      return () => {
        clearTimeout(firstSwapTimer);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isScrambling, showingFirst, swapWords, typewriter, phraseIndex]);

  const renderHeading = (content: string, ref: React.RefObject<HTMLHeadingElement | null>, additionalClasses: string = '') => {
    const headingClassName = `animated-title ${additionalClasses}`;

    if (useHTML) {
      const htmlProps = {
        ref,
        className: headingClassName,
        'data-base': content,
        dangerouslySetInnerHTML: { __html: content }
      };

      switch (headingLevel) {
        case 'h2':
          return <h2 {...htmlProps} />;
        case 'h3':
          return <h3 {...htmlProps} />;
        case 'h4':
          return <h4 {...htmlProps} />;
        case 'h5':
          return <h5 {...htmlProps} />;
        case 'h6':
          return <h6 {...htmlProps} />;
        default:
          return <h1 {...htmlProps} />;
      }
    } else {
      const textProps = {
        ref,
        className: headingClassName,
        'data-base': content
      };

      switch (headingLevel) {
        case 'h2':
          return <h2 {...textProps}>{content}</h2>;
        case 'h3':
          return <h3 {...textProps}>{content}</h3>;
        case 'h4':
          return <h4 {...textProps}>{content}</h4>;
        case 'h5':
          return <h5 {...textProps}>{content}</h5>;
        case 'h6':
          return <h6 {...textProps}>{content}</h6>;
        default:
          return <h1 {...textProps}>{content}</h1>;
      }
    }
  };

  return (
    <>
      {/* Para HTML, renderizar tudo em um elemento */}
      {useHTML ? (
        renderHeading(baseText, titleRef, className)
      ) : (
        <>
          {/* Primeira linha - sempre visível */}
          {renderHeading(firstLine, titleRef, `${maxLines === 2 ? 'first-line' : ''} ${className}`)}

          {/* Segunda linha - aparece quando necessário */}
          {maxLines === 2 && secondLine && renderHeading(secondLine, secondLineRef, `second-line ${showSecondLine ? 'show' : ''}`)}
        </>
      )}

      <div ref={measureRef} className="title-measure" />
    </>
  );
};

export default AnimatedTitle;
